import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { humanizeAxiosError } from "@/lib/api";
import { AttachmentUploader, AttachmentList } from "@/components/Attachments";

export default function Messages() {
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState("user"); // user | department | all
  const [form, setForm] = useState({ to_user_id: "", to_departments: [], subject: "", body: "", attachments: [] });
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/messages/inbox").then((r) => setInbox(r.data));
    api.get("/messages/sent").then((r) => setSent(r.data));
    // Use directory endpoint — returns all approved staff regardless of viewer's role
    api.get("/personnel/directory").then((r) => setUsers((r.data || []).filter((u) => u.id !== user?.id)));
  }, [user?.id]);
  useEffect(() => { load(); }, [load]);

  const departments = Array.from(new Set(users.map((u) => u.department).filter(Boolean)));
  const isPrivileged = ["admin", "hr", "gm"].includes(user?.role);
  const canSendDept = isPrivileged || user?.role === "manager";

  const send = async () => {
    if (!form.subject.trim() || !form.body.trim()) return toast.error("Konu ve mesaj gerekli");
    const payload = { subject: form.subject, body: form.body, attachments: form.attachments };
    if (mode === "user") {
      if (!form.to_user_id) return toast.error("Alıcı seçin");
      payload.to_user_id = form.to_user_id;
    } else if (mode === "department") {
      if (form.to_departments.length === 0) return toast.error("En az bir departman seçin");
      payload.to_departments = form.to_departments;
    } else if (mode === "all") {
      payload.to_all = true;
    }
    setSending(true);
    try {
      const { data } = await api.post("/messages", payload);
      toast.success(`${data.sent} kişiye gönderildi`);
      setOpen(false);
      setForm({ to_user_id: "", to_departments: [], subject: "", body: "", attachments: [] });
      setMode("user");
      load();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setSending(false);
    }
  };

  const toggleDept = (d) => setForm((f) => ({
    ...f,
    to_departments: f.to_departments.includes(d) ? f.to_departments.filter((x) => x !== d) : [...f.to_departments, d],
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">İletişim</div>
          <h1 className="font-display text-3xl font-bold mt-1">Mesajlar</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-msg-btn"><PaperPlaneTilt size={16} className="mr-1" />Yeni Mesaj</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Yeni Mesaj</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="w-full">
                  <TabsTrigger value="user" className="flex-1" data-testid="tab-user">Kayıtlı Personeller</TabsTrigger>
                  {canSendDept && <TabsTrigger value="department" className="flex-1" data-testid="tab-dept">Seçili Departmanlar</TabsTrigger>}
                  {isPrivileged && <TabsTrigger value="all" className="flex-1" data-testid="tab-all">Tümü</TabsTrigger>}
                </TabsList>
              </Tabs>

              {mode === "user" && (
                <div>
                  <Label>Alıcı Personel</Label>
                  <Select value={form.to_user_id} onValueChange={(v) => setForm({...form, to_user_id: v})}>
                    <SelectTrigger data-testid="msg-user"><SelectValue placeholder="Kişi seçin" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.department} ({u.role})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {mode === "department" && (
                <div>
                  <Label>Departmanlar (birden fazla seçilebilir)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto p-2 border border-border rounded">
                    {departments.length === 0 && <div className="text-xs text-muted-foreground col-span-2">Departman bulunamadı</div>}
                    {departments.map((d) => {
                      const disabled = user?.role === "manager" && d !== user.department;
                      return (
                        <div key={d} className="flex items-center gap-2">
                          <Checkbox
                            id={`dept-${d}`}
                            disabled={disabled}
                            checked={form.to_departments.includes(d)}
                            onCheckedChange={() => toggleDept(d)}
                            data-testid={`msg-dept-${d}`}
                          />
                          <Label htmlFor={`dept-${d}`} className={`cursor-pointer text-sm ${disabled ? "opacity-50" : ""}`}>{d}</Label>
                        </div>
                      );
                    })}
                  </div>
                  {form.to_departments.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {form.to_departments.map((d) => <Badge key={d} variant="secondary">{d}</Badge>)}
                    </div>
                  )}
                </div>
              )}

              {mode === "all" && (
                <div className="text-sm bg-secondary p-3 rounded">
                  Bu mesaj, onaylı tüm çalışanlara (kendiniz hariç) gönderilecektir.
                </div>
              )}

              <div><Label>Konu</Label><Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="msg-subject" /></div>
              <div><Label>Mesaj</Label><Textarea rows={5} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="msg-body" /></div>
              <div><Label>Ekler</Label><AttachmentUploader value={form.attachments} onChange={(atts) => setForm({...form, attachments: atts})} testId="msg-attach" /></div>
              <Button onClick={send} disabled={sending} className="w-full" data-testid="msg-send">
                {sending ? "Gönderiliyor…" : "Gönder"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="inbox">
        <TabsList><TabsTrigger value="inbox">Gelen ({inbox.filter((m) => !m.read).length})</TabsTrigger><TabsTrigger value="sent">Gönderilen</TabsTrigger></TabsList>
        <TabsContent value="inbox">
          <MessageList list={inbox} onRead={(id) => api.post(`/messages/${id}/read`).then(load)} />
        </TabsContent>
        <TabsContent value="sent"><MessageList list={sent} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MessageList({ list, onRead }) {
  return (
    <div className="space-y-2">
      {list.length === 0 && <div className="text-muted-foreground text-sm">Mesaj yok</div>}
      {list.map((m) => (
        <Card key={m.id} onClick={() => onRead?.(m.id)} className={`cursor-pointer ${!m.read && onRead ? "border-primary" : ""}`}>
          <CardContent className="p-4">
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{m.from_name}</div>
                <div className="font-semibold">{m.subject}</div>
                <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{m.body}</div>
                <AttachmentList attachments={m.attachments} testId={`msg-atts-${m.id}`} />
              </div>
              <div className="text-xs text-muted-foreground shrink-0">{new Date(m.sent_at).toLocaleString("tr-TR")}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
